import React, { useState, useEffect } from 'react';
import { parseISO } from 'date-fns';
import { getClientHours, refreshClientHours, getClientTimeEntries, getClientWikiUrl, getAnnualClientHours } from './api';
import './ClientesHoras.css';

function ClientesHoras() {
  const [viewMode, setViewMode] = useState('mensual'); // 'mensual' | 'anual'
  const [data, setData] = useState(null);
  const [annualData, setAnnualData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [clientEntries, setClientEntries] = useState({});
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    if (viewMode === 'mensual') {
      loadData();
    } else {
      loadAnnualData();
    }
  }, [selectedMonth, selectedYear, viewMode]);

  const loadAnnualData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAnnualClientHours(selectedYear);
      setAnnualData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error cargando datos anuales');
      console.error('[ClientesHoras] Error anual:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[ClientesHoras] Cargando datos...', { selectedYear, selectedMonth });
      const response = await getClientHours(selectedYear, selectedMonth);
      console.log('[ClientesHoras] Datos recibidos:', response.data);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error cargando datos');
      console.error('[ClientesHoras] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await refreshClientHours(selectedYear, selectedMonth);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error refrescando datos');
      console.error('[ClientesHoras] Error refreshing:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Nunca';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (percentage) => {
    if (!percentage) return 'no-hours';
    if (percentage > 100) return 'exceeded';
    if (percentage >= 85) return 'warning';
    return 'ok';
  };

  const toggleClientExpansion = async (clientId) => {
    if (expandedClientId === clientId) {
      // Colapsar
      setExpandedClientId(null);
    } else {
      // Expandir
      setExpandedClientId(clientId);

      // Si no tenemos los entries cacheados, cargarlos
      if (!clientEntries[clientId]) {
        setLoadingEntries(true);
        try {
          const response = await getClientTimeEntries(clientId, selectedYear, selectedMonth);
          setClientEntries(prev => ({
            ...prev,
            [clientId]: response.data.entries
          }));
        } catch (err) {
          console.error('[ClientesHoras] Error cargando time entries:', err);
        } finally {
          setLoadingEntries(false);
        }
      }
    }
  };

  const openWikiPage = async (client, event) => {
    event.stopPropagation(); // Evitar que se expanda/colapse la fila
    try {
      const response = await getClientWikiUrl(client.id, client.identifier, client.name);
      if (response.data && response.data.wikiUrl) {
        window.open(response.data.wikiUrl, '_blank');
      }
    } catch (err) {
      console.error('[ClientesHoras] Error obteniendo URL de Wiki.js:', err);
      alert('No se pudo obtener la URL de la documentación para este cliente');
    }
  };

  if (loading) {
    return (
      <div className="clientes-horas-container">
        <div className="loading">Cargando datos de clientes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clientes-horas-container">
        <div className="error">Error: {error}</div>
        <button onClick={loadData} className="retry-button">Reintentar</button>
      </div>
    );
  }

  if (viewMode === 'mensual' && !data) return null;
  if (viewMode === 'anual' && !annualData) return null;

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="clientes-horas-container">
      <div className="clientes-horas-header">
        <div>
          <h2>Clientes / Horas</h2>
          <div className="view-mode-tabs">
            <button
              className={`view-mode-tab ${viewMode === 'mensual' ? 'active' : ''}`}
              onClick={() => setViewMode('mensual')}
            >
              Mensual
            </button>
            <button
              className={`view-mode-tab ${viewMode === 'anual' ? 'active' : ''}`}
              onClick={() => setViewMode('anual')}
              title="Desviación acumulada del año"
            >
              Anual (Deuda YTD)
            </button>
          </div>
          {viewMode === 'mensual' && data?.cachedAt && (
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              Última actualización: {formatTimestamp(data.cachedAt)}
            </div>
          )}
          {viewMode === 'anual' && annualData && (
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              Datos hasta mes {annualData.upToMonth} de {annualData.year}
            </div>
          )}
        </div>
        <div className="month-selector">
          {viewMode === 'mensual' && (
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
              {months.map((month, index) => (
                <option key={index + 1} value={index + 1}>{month}</option>
              ))}
            </select>
          )}
          <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          {viewMode === 'mensual' && (
          <button
            onClick={handleRefresh}
            className={`refresh-button ${refreshing ? 'loading' : ''}`}
            title="Recargar datos desde Redmine"
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span className="spinner"></span>
                Cargando...
              </>
            ) : (
              <>
                🔄 Recargar Datos
              </>
            )}
          </button>
          )}
        </div>
      </div>

      {viewMode === 'anual' && annualData && (
        <AnnualView data={annualData} />
      )}

      {viewMode === 'mensual' && data && data.totals && data.totals.totalAssigned > 0 && (
        <div className="totals-summary">
          <div className="total-card">
            <span className="total-label">Total Asignado:</span>
            <span className="total-value">{data.totals.totalAssigned}h</span>
          </div>
          <div className="total-card">
            <span className="total-label">Total Consumido:</span>
            <span className="total-value">{data.totals.totalConsumed.toFixed(1)}h</span>
          </div>
          <div className={`total-card ${getStatusClass(data.totals.totalPercentage)}`}>
            <span className="total-label">Consumo Total:</span>
            <span className="total-value">{data.totals.totalPercentage}%</span>
          </div>
        </div>
      )}

      {viewMode === 'mensual' && data && (
      <div className="clients-table-container">
        <table className="clients-table">
          <thead>
            <tr>
              <th>
                Cliente
              </th>
              <th className="text-right">
                Horas Asignadas
                <span className="info-icon" title="Horas contratadas mensualmente por el cliente">ℹ️</span>
              </th>
              <th className="text-right">
                Horas Consumidas
                <span className="info-icon" title="Horas trabajadas en el mes actual para este cliente">ℹ️</span>
              </th>
              <th className="text-right">
                Consumo Real
                <span className="info-icon" title="Porcentaje de horas consumidas respecto a las contratadas">ℹ️</span>
              </th>
              <th className="text-right">
                Horas Estimadas
                <span className="info-icon" title="Horas que deberían haberse consumido hasta hoy según el ritmo del mes: (horas_contratadas × días_transcurridos / días_totales_mes)">ℹ️</span>
              </th>
              <th className="text-right">
                Restantes
                <span className="info-icon" title="Horas que quedan disponibles en el mes actual">ℹ️</span>
              </th>
              <th className="text-right">
                Balance Anterior
                <span className="info-icon" title="Balance con el que comenzó el mes (custom field 29 de Redmine)">ℹ️</span>
              </th>
              <th className="text-right">
                Balance Actualizado
                <span className="info-icon" title="Balance calculado actual: balance_anterior - (consumidas - contratadas)">ℹ️</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.clients && data.clients.length > 0 ? (
              data.clients.map(client => (
                <React.Fragment key={client.id}>
                  <tr className={`client-row ${getStatusClass(client.percentage)}`} onClick={() => toggleClientExpansion(client.id)}>
                    <td className="client-name">
                      <span className={`expand-icon ${expandedClientId === client.id ? 'expanded' : ''}`}>
                        ▶
                      </span>
                      {client.name}
                      <span
                        className="wiki-icon"
                        onClick={(e) => openWikiPage(client, e)}
                        title="Ver documentación en Wiki.js"
                        style={{ marginLeft: '10px', cursor: 'pointer', fontSize: '16px' }}
                      >
                        📚
                      </span>
                    </td>
                    <td className="text-right">
                      {client.monthlyHours ? `${client.monthlyHours}h` : '-'}
                    </td>
                    <td className="text-right">
                      {client.consumedHours.toFixed(1)}h
                    </td>
                    <td className="text-right">
                      {client.percentage !== null ? (
                        <span className={`percentage-badge ${getStatusClass(client.percentage)}`}>
                          {client.percentage}%
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-right">
                      {client.estimatedHours !== null ? (
                        <span className="estimated-hours">
                          {client.estimatedHours.toFixed(1)}h
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-right">
                      {client.remaining !== null ? (
                        <span className={client.remaining < 0 ? 'text-negative' : ''}>
                          {client.remaining.toFixed(1)}h
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-right">
                      {client.previousBalance !== null && client.previousBalance !== undefined ? (
                        <span className={client.previousBalance < 0 ? 'text-positive' : client.previousBalance > 0 ? 'text-negative' : ''}>
                          {client.previousBalance.toFixed(1)}h
                        </span>
                      ) : '0.0h'}
                    </td>
                    <td className="text-right">
                      {client.balance !== null && client.balance !== undefined ? (
                        <span className={client.balance < 0 ? 'text-positive' : client.balance > 0 ? 'text-negative' : ''}>
                          {client.balance.toFixed(1)}h
                        </span>
                      ) : '0.0h'}
                    </td>
                  </tr>
                  {expandedClientId === client.id && (
                    <tr className="entries-row">
                      <td colSpan="8" className="entries-cell">
                        <div className="entries-container">
                          <div className="entries-title">Tareas imputadas en {client.name}</div>
                          {loadingEntries ? (
                            <div className="entries-loading">Cargando tareas...</div>
                          ) : clientEntries[client.id] && clientEntries[client.id].length > 0 ? (
                            <table className="entries-table">
                              <thead>
                                <tr>
                                  <th>Fecha</th>
                                  <th>Usuario</th>
                                  <th>Issue</th>
                                  <th>Actividad</th>
                                  <th>Comentarios</th>
                                  <th style={{ textAlign: 'right' }}>Horas</th>
                                </tr>
                              </thead>
                              <tbody>
                                {clientEntries[client.id].map((entry, index) => (
                                  <tr key={index}>
                                    <td>{parseISO(entry.entryDate).toLocaleDateString('es-ES')}</td>
                                    <td>{entry.redmineUserName}</td>
                                    <td>{entry.issueSubject || 'Sin issue'}</td>
                                    <td>{entry.activityName}</td>
                                    <td>{entry.comments || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>{entry.hours.toFixed(2)}h</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="entries-empty">No hay tareas imputadas para este cliente en el período seleccionado</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No hay datos para el período seleccionado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {viewMode === 'mensual' && (
      <div className="legend">
        <div className="legend-item">
          <span className="legend-color ok"></span>
          <span>Consumo normal (&lt; 85%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color warning"></span>
          <span>Consumo alto (85-100%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color exceeded"></span>
          <span>Horas excedidas (&gt; 100%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color no-hours"></span>
          <span>Sin horas asignadas</span>
        </div>
      </div>
      )}
    </div>
  );
}

const MONTH_ABBR = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function AnnualView({ data }) {
  const { kpi, clients, upToMonth, year } = data;
  const monthList = Array.from({ length: upToMonth }, (_, i) => i + 1);

  const getDeviationClass = (dev) => {
    if (dev === null || dev === undefined) return 'no-hours';
    const abs = Math.abs(dev);
    if (abs >= 10) return 'exceeded';
    if (abs >= 5) return 'warning';
    return 'ok';
  };

  const formatHours = (h) => `${h >= 0 ? '' : ''}${h.toFixed(1)}h`;
  const formatDelta = (d) => `${d > 0 ? '+' : ''}${d.toFixed(1)}h`;

  return (
    <div className="annual-view">
      <div className="totals-summary">
        <div className={`total-card ${kpi.percentageWithinTarget >= 70 ? 'ok' : kpi.percentageWithinTarget >= 50 ? 'warning' : 'exceeded'}`}>
          <span className="total-label">Clientes dentro del objetivo (&lt;10% desviación)</span>
          <span className="total-value">{kpi.percentageWithinTarget}%</span>
          <span style={{ fontSize: '10px', color: '#666' }}>
            {kpi.clientsWithinTarget} de {kpi.totalClients} clientes
          </span>
        </div>
        <div className="total-card">
          <span className="total-label">Total contratado YTD</span>
          <span className="total-value">{kpi.totalContracted.toFixed(1)}h</span>
        </div>
        <div className="total-card">
          <span className="total-label">Total consumido YTD</span>
          <span className="total-value">{kpi.totalConsumed.toFixed(1)}h</span>
        </div>
        <div className={`total-card ${getDeviationClass(kpi.globalDeviation)}`}>
          <span className="total-label">Desviación global</span>
          <span className="total-value">{kpi.globalDeviation > 0 ? '+' : ''}{kpi.globalDeviation}%</span>
        </div>
      </div>

      <div className="clients-table-container">
        <table className="clients-table annual-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th className="text-right" title="Horas contratadas acumuladas en el año">Contratado YTD</th>
              <th className="text-right" title="Horas consumidas acumuladas en el año">Consumido YTD</th>
              <th className="text-right" title="Diferencia acumulada: consumido - contratado">Δ Acum.</th>
              <th className="text-right" title="Desviación % respecto al contratado YTD. Objetivo: |desviación| &lt; 10%">Desviación</th>
              {monthList.map(m => (
                <th key={m} className="text-right month-col" title={`${MONTH_ABBR[m-1]} ${year}`}>
                  {MONTH_ABBR[m-1]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr><td colSpan={5 + monthList.length} className="no-data">Sin datos para {year}. Necesitas cargar meses en la vista mensual primero.</td></tr>
            ) : (
              clients.map(c => (
                <tr key={c.id} className={`client-row ${getDeviationClass(c.deviationPercentage)}`}>
                  <td className="client-name">{c.name}</td>
                  <td className="text-right">{c.contractedYTD.toFixed(1)}h</td>
                  <td className="text-right">{c.consumedYTD.toFixed(1)}h</td>
                  <td className="text-right">
                    <span className={c.deltaYTD > 0 ? 'text-negative' : c.deltaYTD < 0 ? 'text-positive' : ''}>
                      {formatDelta(c.deltaYTD)}
                    </span>
                  </td>
                  <td className="text-right">
                    {c.deviationPercentage !== null ? (
                      <span className={`percentage-badge ${getDeviationClass(c.deviationPercentage)}`}>
                        {c.deviationPercentage > 0 ? '+' : ''}{c.deviationPercentage}%
                      </span>
                    ) : '-'}
                  </td>
                  {monthList.map(m => {
                    const md = c.months[m];
                    if (!md) {
                      return <td key={m} className="text-right month-cell empty" title="Sin datos cargados">—</td>;
                    }
                    const delta = md.delta;
                    const cls = delta > 0.01 ? 'over' : delta < -0.01 ? 'under' : 'even';
                    return (
                      <td key={m} className={`text-right month-cell ${cls}`}
                          title={`${MONTH_ABBR[m-1]}: ${md.consumed.toFixed(1)}h / ${md.contracted.toFixed(1)}h`}>
                        {formatDelta(delta)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="legend">
        <div className="legend-item"><span className="legend-color ok"></span><span>Desviación &lt; 5%</span></div>
        <div className="legend-item"><span className="legend-color warning"></span><span>Desviación 5-10%</span></div>
        <div className="legend-item"><span className="legend-color exceeded"></span><span>Desviación ≥ 10% (fuera de objetivo)</span></div>
      </div>
      <div style={{ fontSize: '11px', color: '#666', marginTop: '10px', padding: '10px', background: '#f9f9f9', borderRadius: '4px' }}>
        <strong>Nota:</strong> los datos anuales se agregan del histórico cacheado en la vista mensual.
        Meses no visitados desde <em>Recargar Datos</em> no aparecerán aquí. Cada celda mensual muestra el delta (consumido − contratado): valores positivos indican exceso de consumo.
      </div>
    </div>
  );
}

export default ClientesHoras;
